import { Inject, Injectable } from '@angular/core';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { Column, FormControlOptions, FormControlOptionsConfig, Section } from '../models';
import { compose } from '../utils/compose';
import { deepMerge } from '../utils/merge-utils';

/**
 * This service scans configuration of section and columns to build group entries on
 * nested forms or array forms so they are available when client component receiving a section
 * can have access to a formGroup that can be added/removed from/to the main form
 */
@Injectable()
export class CatalogFormBuilderService {
  private _sectionsCache = {};

  constructor(@Inject(CbreLoggerToken) private logger: CbreLogger) {}

  /**
   * It scans Section and each Column in given section configs for form group entries,
   * if any found it attaches the group to the formControlOptions of each level
   * Section or Column
   * @param sections
   */
  public mapControlOptions(
    sections: Section[],
    formConfigs: { [key: string]: FormControlOptionsConfig } = {}
  ): Section[] {
    this._sectionsCache = Object.assign({}, formConfigs);
    return sections.map(this.sectionMapper);
  }

  private get sectionMapper() {
    return compose(this._mapSectionControlOpts, this._mapSectionColumnControlTypes);
  }

  _mapSectionControlOpts = (section: Section) => {
    const providedControlOpts = section.formControlOptions;
    let lastParent: FormControlOptions<Section>;
    let rootAttr = null;

    section.columns.forEach(
      col => ({ lastParent, rootAttr } = this._mapSectionControlOptionsByColumn(lastParent, rootAttr, col))
    );

    const generatedControlOptions = this.generateFormControlOptionsFromCache(section, rootAttr, providedControlOpts);
    const { targetedControlOptions, formControlOptions } = generatedControlOptions;
    // return section with generatedFormControlOpts
    return { ...section, formControlOptions, targetedControlOptions };
  };

  private _mapSectionControlOptionsByColumn = (
    lastParent: FormControlOptions<Section>,
    rootAttr: string,
    col: Column
  ) => {
    lastParent = this._sectionsCache;
    // flat all paths
    const paths = this._getFlatPaths(col.field);
    // if there is at least one path deep in the column (e.g. x/y, where x means one object deep)
    if (paths.length > 1) {
      const [firstPath] = paths;
      rootAttr = firstPath;
      const treeFormControlOptionBuilder = this._makeFormControlOptionLevelProcessor(col, paths);
      paths.forEach((path, index) => (lastParent = treeFormControlOptionBuilder(path, index, lastParent)));
    }
    return { lastParent, rootAttr };
  };

  private _makeFormControlOptionLevelProcessor =
    (col: Column, paths: string[]) => (path: string, index: number, lastParent: FormControlOptions<Section>) => {
      if (lastParent?.children && lastParent.children[path] != null) {
        lastParent = lastParent.children[path];
      }
      // else generate either a controlConfigs (if path is leaf) or a next branch of controlOptions (children for lastParent)
      else if (this._isControlLeaf(index, paths)) {
        // if path is leaf, then merge controlOptions (leaf is last path in the field)
        lastParent = this.generateControlToLastParent(lastParent, path, col, index);
      } else {
        // generate next level
        lastParent = this._buildNextFormControlOptionLevel(path, index, col, lastParent);
      }

      return lastParent;
    };

  private _buildNextFormControlOptionLevel = (
    path: string,
    index: number,
    col: Column,
    lastParent: FormControlOptions<Section>
  ) => {
    // generate next children
    const nameAndType = { name: path, type: this._getPathType(col.field, path) };
    const nextControlOpt = deepMerge(nameAndType, lastParent[path] ?? {});
    // get last parent based on next children
    return this.generateNextChildren(lastParent, nextControlOpt, path, index);
  };

  /**
   * Generates controlsConfig and merges with existing for control (leafs)
   * @param lastParent
   * @param path
   * @param col
   * @param index
   * @returns
   */
  private generateControlToLastParent(
    lastParent: FormControlOptions<Section<Column>>,
    path: string,
    col: Column,
    index: number
  ) {
    const nextControlOpt = lastParent;
    let nextControlConfigs = { ...nextControlOpt.controlsConfig, [path]: [null, col.validatorFns()] };
    if (col.unit) {
      const uomPaths = this._getFlatPaths(col.unit.field);
      const uomPath = uomPaths[index];
      nextControlConfigs = { ...nextControlConfigs, [uomPath]: [null, col.unit.validatorFns()] };
    }
    lastParent.controlsConfig = nextControlConfigs;
    return lastParent;
  }

  /**
   * Generates next controlOptions for non leaf items (array / groups)
   * @param lastParent
   * @param nextControlOpt
   * @param path
   * @param index
   * @returns
   */
  private generateNextChildren(
    lastParent: FormControlOptions<Section>,
    nextControlOpt: FormControlOptions<Section>,
    path: string,
    index: number
  ) {
    // add to cache
    if (index === 0) {
      lastParent[path] = nextControlOpt;
      // for root attr/path, just assign generated as lastParent for next path value
      lastParent = nextControlOpt;
    } else {
      // on children, then find from lastParent children and check if already exist
      if (!lastParent.children) {
        lastParent.children = {};
      }
      const existingChild = lastParent.children[path];
      if (existingChild != null) {
        lastParent = existingChild;
      } else {
        /*
         * if children does not exist, then push as new entry
         * and update lastParent with nextControlOpts for next path */
        lastParent.children[path] = nextControlOpt;
        lastParent = nextControlOpt;
      }
    }
    return lastParent;
  }

  /**
   * Return targetedControlOptions and formControlOptions based on rootAtt and the generated sectionCache objects
   * @param section
   * @param rootAttr
   * @param providedControlOpts
   * @returns
   */
  private generateFormControlOptionsFromCache(
    section: Section,
    rootAttr: string,
    providedControlOpts: FormControlOptions<any>
  ) {
    let targetedControlOptions: { [key: string]: FormControlOptions<any> };
    let formControlOptions: FormControlOptions<any> = null;

    const rootPaths = this.getSectionControlOptionTargets(section);
    if (rootPaths?.length > 0) {
      const providedTargetedOpts = section.targetedControlOptions ?? {};
      targetedControlOptions = deepMerge(
        providedTargetedOpts,
        Object.assign({}, ...rootPaths.map(path => ({ [path]: this._sectionsCache[path] })))
      );
      formControlOptions = targetedControlOptions[rootPaths[0]];
    }
    // get form cache the formControlOption with rootAttr as key
    formControlOptions = this._sectionsCache[rootAttr];
    if (providedControlOpts != null) {
      // if controlOptions provided on config, merge with generated
      const fcoSources = [formControlOptions, providedControlOpts];
      if (targetedControlOptions) {
        fcoSources.push(targetedControlOptions[providedControlOpts.name]);
      }
      formControlOptions = fcoSources.filter(Boolean).reduce((prev, next) => deepMerge(prev, next), {});
    }

    return { targetedControlOptions, formControlOptions };
  }

  /**
   * Splits column.field by
   * @param col
   * @returns
   */
  private _getFlatPaths = (field: string): string[] => {
    return field
      .split('/')
      .map(subName => subName.split('.'))
      .reduce((acc, p) => [...acc, ...p], []);
  };

  /**
   * Return sub-group type of a field(substring of full field/col.field)
   * @param field
   * @param path
   * @returns
   */
  private _getPathType = (field: string, path: string) => {
    const idxArray = field.indexOf(`${path}/`);
    const idxNested = field.indexOf(`${path}.`);
    return this._isArrayField(idxArray, idxNested) ? 'array' : 'group';
  };

  /**
   * Return wether if index is the last element in paths array
   * @param index
   * @param paths
   * @returns
   */
  private _isControlLeaf = (index: number, paths: string[]) => {
    return index === paths.length - 1;
  };

  private _isArrayField(idxOfArrays: number, idxOfNested: number) {
    return idxOfArrays > -1 && (idxOfArrays < idxOfNested || idxOfNested === -1);
  }

  /**
   * maps section columns with _mapColumnFormControlOptions to scan entries if formControlOptions is provided
   * @param section
   */
  private _mapSectionColumnControlTypes = (section: Section): Section => {
    // calculate column formControlOptions
    const nextColumns = section.columns.map(col => {
      const paths = this._getFlatPaths(col.field);
      const [rootPath] = paths;

      const nextTargetControlOpts = section.targetedControlOptions && section.targetedControlOptions[rootPath];
      const rootFormControlOptions = nextTargetControlOpts ?? null;

      return {
        ...this._mapColumnFormControlOptions(col, rootFormControlOptions),
        unit: col.unit ? this._mapColumnFormControlOptions(col.unit, rootFormControlOptions) : undefined,
        related: col.related?.map(relCol => this._mapColumnFormControlOptions(relCol, rootFormControlOptions)),
      };
    });

    // get common path
    const path = section.path ?? this._calculateRootPath(section);
    const isPathRequired = section.columns.every(c => c.field.includes('.') || c.field.includes('/'));
    if (isPathRequired && path == null) {
      this.logger.warn(
        `DEVELOPER ATTENTION: missing path or not derived properly, please check the column paths for section
        ${section.sectionId} or configure one manually in sections override`
      );
    }
    return {
      ...section,
      columns: nextColumns,
      path,
    };
  };

  private _calculateRootPath(section: Section): string {
    const toFlatPathsAndFirstElement = compose(
      // transform to flat paths
      col => this._getFlatPaths(col.getReadPath()),
      // get first element
      (x: string[]) => x[0]
    );
    const allColumnPaths = section.columns.map(toFlatPathsAndFirstElement);
    const allColumnPathSet = new Set(allColumnPaths);
    if (allColumnPathSet.size === 1) {
      const entry = allColumnPathSet.values().next();
      return entry.value;
    }
    return null;
  }

  /**
   * fills formControlOptions for column based on field first splitter context
   * '.' nested form and '/' for arrays. if section has groupControl it attaches
   * the controlGroup for possible reference.
   * @param section
   */
  private _mapColumnFormControlOptions = (col: Column, rootFormControlOptions: FormControlOptions<Section>): Column => {
    const idxOfArrays = col.field.lastIndexOf('/');
    const idxOfNested = col.field.lastIndexOf('.');
    let controlName = col.field;
    if (idxOfArrays > idxOfNested) {
      const arrPath = col.field.split('/');
      const [last] = [...arrPath].reverse();
      controlName = last;
    } else if (idxOfNested > idxOfArrays) {
      const nestedPath = col.field.split('.');
      const [last] = [...nestedPath].reverse();
      controlName = last;
    }
    const { formControlOptions = {} } = col;

    const nextFormControlOptions = {
      name: controlName,
      type: 'control',
      parentControl: rootFormControlOptions ? this._getControlParentOptions(col) : null,
    };
    const mergedControlOptions = deepMerge(formControlOptions, nextFormControlOptions);
    return {
      ...col,
      formControlOptions: mergedControlOptions,
    };
  };

  /**
   * Finds the unique path for column field and binds the refs to parentControlOptions
   * @param col
   * @param rootFormControlOpts
   * @returns
   */
  private _getControlParentOptions(col: Column): FormControlOptions<Section> {
    // get up to the last field before the control name
    const fields = this._getFlatPaths(col.field);
    const controlPath = fields.slice(0, fields.length - 1);

    // if no deep levels nothing to do, continue
    if (controlPath.length === 0) return null;

    // declare tmp variables
    let rootParentControlConfigs: FormControlOptions<Section>;
    let lastParentControlConfigs: FormControlOptions<Section>;
    let subLevelControlConfigs: FormControlOptions<Section> = this._sectionsCache;
    const buildNextControlConfigs = this._makeSublevelsAndLastParentControlConfigBuilder(col);
    controlPath.forEach((field, index) => {
      const nextControlLevels = buildNextControlConfigs(
        subLevelControlConfigs,
        rootParentControlConfigs,
        lastParentControlConfigs,
        field,
        index
      );
      subLevelControlConfigs = nextControlLevels.subLevelControlConfigs;
      lastParentControlConfigs = nextControlLevels.lastParentControlConfigs;
      rootParentControlConfigs = nextControlLevels.rootParentControlConfigs;
    });
    return rootParentControlConfigs;
  }

  private _makeSublevelsAndLastParentControlConfigBuilder = (col: Column) => {
    const isConditional = col.path != null;
    const conditionalPathsSplit = isConditional ? col.path.split('.') : null;
    return (
      subLevelControlConfigs: FormControlOptions<Section>,
      rootParentControlConfigs: FormControlOptions<Section>,
      lastParentControlConfigs: FormControlOptions<Section>,
      field: string,
      index: number
    ) => {
      const currentControlConfigs = subLevelControlConfigs != null ? subLevelControlConfigs[field] : null;
      if (currentControlConfigs != null) {
        const { children: _children, ...nextControlConfig } = currentControlConfigs;
        const nextRootAndParentControlConfigsBuilder = this._getNextRootAndParentControlConfigs(
          field,
          index,
          nextControlConfig
        );
        ({ rootParentControlConfigs, lastParentControlConfigs } = nextRootAndParentControlConfigsBuilder(
          lastParentControlConfigs,
          rootParentControlConfigs
        ));

        // is conditional and column has a condition
        if (isConditional && conditionalPathsSplit[index]?.indexOf('[') > 0) {
          // if att name contains square braces, it means the condition is applied at this level of sub elements
          const targetControlValues = this._getTargetControlValues(col, field);
          // intentional mutation
          lastParentControlConfigs = Object.assign(lastParentControlConfigs, targetControlValues);
        }
        subLevelControlConfigs = currentControlConfigs.children;
      }

      return { subLevelControlConfigs, rootParentControlConfigs, lastParentControlConfigs };
    };
  };

  private _getNextRootAndParentControlConfigs =
    (field: string, index: number, nextControlConfig: FormControlOptions<Section>) =>
    (lastParentControlConfigs: FormControlOptions<Section>, rootParentControlConfigs: FormControlOptions<Section>) => {
      if (index === 0) {
        return { rootParentControlConfigs: nextControlConfig, lastParentControlConfigs: nextControlConfig };
      } else if (lastParentControlConfigs) {
        lastParentControlConfigs.children = { [field]: nextControlConfig };
        lastParentControlConfigs = nextControlConfig;
      }
      return { lastParentControlConfigs, rootParentControlConfigs };
    };

  /**
   * Based on column condition generate a path and control values
   * @param col
   * @param name
   * @returns
   */
  private _getTargetControlValues(col: Column, name: string) {
    const { path } = col;
    const basePath = path.substring(path.indexOf(name) + name.length);
    const targetControlPath = basePath.substring(0, basePath.indexOf('].') + 1);

    if (targetControlPath) {
      const targetControlValues = this._makeTargetControlValues(targetControlPath);
      return { targetControlValues, targetControlPath };
    }

    return {};
  }

  /**
   * Parses a string into it's native equivalent.
   * DEVELOPER NOTE: Use this only to decompose catalog conditions into values.
   * is not meant to be all purposes but limited to catalog conditional paths.
   * Try/catch had to be applied to handle unexpected JSON error
   * @example
   * _ensureValueType('true')   // true   <= boolean
   * _ensureValueType('false')  // false  <= boolean
   * _ensureValueType('123')    // 123    <= number
   * _ensureValueType("'holo'") // "holo" <= string
   * @param v
   * @returns
   */
  private _ensureValueType = (v: string) => {
    try {
      return v ? JSON.parse(v.replace(/\'/g, '"')) : null;
    } catch (e) {
      this.logger.error(e);
      return null;
    }
  };

  private _makeTargetControlValues = (path: string) => {
    return (
      this._splitConditions(path)
        // remove [ & ]
        .map(condition => condition.replace(/\[/g, '').replace(/\]/g, ''))
        // filter out conditions that include "!="
        .filter(x => !x.includes('!='))
        // trim and split
        .map(x => x.trim().split('='))
        // ensure conditions found are k,v to produce target control values
        .filter(x => x.length === 2)
        // build the key value object
        .map(([k, v]) => ({ [k.trim()]: this._ensureValueType(v) }))
        // reduce to single object
        .reduce((a, c) => ({ ...a, ...c }), {})
    );
  };

  /**
   * Generates target subset path and value of data within a collection of data
   * generated path and values are useful to find form groups with specific values
   * or generate groups with minimum required and fixed data
   * @param section
   * @param targetControls
   * @returns
   */
  public mapTargetControlOptions(section: Section, targetControls: string): Section {
    const filteredSection = {
      ...section,
      columns: section.columns.filter(col => {
        const [rootPath] = this._getFlatPaths(col.field);
        return col.field.startsWith(targetControls) || targetControls.startsWith(rootPath);
      }),
    };
    return this.sectionMapper(filteredSection);
  }

  /**
   * Returns all sub elements (groups/form arrays) in a section based on all distinct column root path.
   * It first scan all possible nested elements within given section and for each sub element it
   * filter columns by each sub element key and creates a K,F where k is a string and F is a FormControlOptions
   * @param section
   * @returns
   */
  public getAllSectionFormControlOptions(section: Section): Array<FormControlOptions<Section>> {
    const basePaths = this.getSectionControlOptionTargets(section);
    const allSectionFormControlOpts = basePaths
      .map(basePath => {
        const nextSection = this.mapTargetControlOptions(section, basePath);
        return nextSection.formControlOptions;
      })
      .filter(Boolean);
    return allSectionFormControlOpts.length ? allSectionFormControlOpts : null;
  }

  /**
   * Returns all possible distinct nested elements in a section
   * @example
   * // given
   * const section = {title:'foo', columns: [{field: 'foo/bar'}, {field:'baz.bazbaz'}]}
   * // when
   * const res = getSectionControlOptionTargets(section)
   * // then
   * res // [foo, baz]  <= meaning section has two nested elements of either form group or form array
   * @param section
   * @returns
   */
  protected getSectionControlOptionTargets(section: Section): string[] {
    const basePaths = new Set<string>();
    section.columns
      .map(col => this._getFlatPaths(col.field))
      .filter(paths => paths.length > 1)
      .forEach(([basePath]) => basePaths.add(basePath));
    return Array.from(basePaths);
  }

  private _splitConditions(conditionString) {
    const conditions = [];
    let currentCondition = '';
    let inQuote = false;
    let quoteType = null; // Store the type of quote (' or ")

    for (let i = 0; i < conditionString.length; i++) {
      const char = conditionString[i];

      if (char === "'" || char === '"') {
        if (inQuote && char === quoteType && conditionString[i - 1] !== '\\') {
          // Closing quote
          inQuote = false;
          quoteType = null;
          currentCondition += char;
        } else if (!inQuote) {
          // Opening quote
          inQuote = true;
          quoteType = char;
          currentCondition += char;
        } else {
          // Quote inside a quote (escaped or different type)
          currentCondition += char;
        }
      } else if (
        !inQuote &&
        conditionString.slice(i, i + 3).toLowerCase() === 'and' &&
        !/\w/.test(conditionString[i - 1]) &&
        !/\w/.test(conditionString[i + 3])
      ) {
        conditions.push(currentCondition.trim());
        currentCondition = '';
        i += 2; // Skip "and"
      } else if (
        !inQuote &&
        conditionString.slice(i, i + 2).toLowerCase() === 'or' &&
        !/\w/.test(conditionString[i - 1]) &&
        !/\w/.test(conditionString[i + 2])
      ) {
        conditions.push(currentCondition.trim());
        currentCondition = '';
        i += 1; // Skip "or"
      } else {
        currentCondition += char;
      }
    }

    conditions.push(currentCondition.trim());
    return conditions;
  }
}
